import { ref, computed } from "vue"
import { API, onTokensChanged } from "./api.js"

// ---------------------------------------------------------------------------
// Admin session auth
// ---------------------------------------------------------------------------
export const adminLoggedIn = ref(false)
export async function checkAuth() {
  try {
    const data = await API.get("/api/admin/me")
    adminLoggedIn.value = !!data.admin
    // if JWT is active, treat as having at least partial admin access
    if (data.via_jwt && data.permissions) {
      adminPermissions.value = data.permissions
    }
  } catch {
    adminLoggedIn.value = false
  }
}

// ---------------------------------------------------------------------------
// Nutritionist session auth + JWT tokens
// ---------------------------------------------------------------------------
export const nLoggedIn    = ref(false)
export const nUser        = ref(null)
export const nPermissions = ref({})
export const nToken       = ref(localStorage.getItem("nsp_access_token") || "")
export const nRefreshToken = ref(localStorage.getItem("nsp_refresh_token") || "")

/** Permissions granted to the current nutritionist via JWT. */
export const adminPermissions = ref({})

/** True if the nutritionist has at least one admin-level permission. */
export const hasAnyAdminPerm = computed(() =>
  Object.values(adminPermissions.value).some(Boolean)
)

/**
 * Whether the cabinet's "Доступ" tab is any use: it exchanges the session for
 * an admin token, which only carries the nutritionist's own permissions. With
 * none (from /api/n/me, role mappings included) there is nothing to get.
 */
export const canGetAdminAccess = computed(() =>
  Object.values(nPermissions.value).some(Boolean) || Object.values(adminPermissions.value).some(Boolean)
)

export function hasPerm(perm) {
  return !!adminPermissions.value[perm]
}

export function storeTokens(accessToken, refreshToken, permissions) {
  nToken.value = accessToken
  nRefreshToken.value = refreshToken
  adminPermissions.value = permissions || {}
  localStorage.setItem("nsp_access_token", accessToken)
  if (refreshToken) localStorage.setItem("nsp_refresh_token", refreshToken)
}

export function clearTokens() {
  nToken.value = ""
  nRefreshToken.value = ""
  adminPermissions.value = {}
  localStorage.removeItem("nsp_access_token")
  localStorage.removeItem("nsp_refresh_token")
}

// Listeners fired when the session dies (refresh failed). The router subscribes
// to re-evaluate guards and redirect off any now-forbidden route.
const _sessionExpiredListeners = []
export function onSessionExpired(fn) { _sessionExpiredListeners.push(fn) }

// Keep reactive state in sync when api.js silently refreshes tokens on 401
// (payload = token response) or drops the session after a failed refresh (null).
onTokensChanged((data) => {
  if (data) {
    nToken.value = data.access_token
    if (data.refresh_token) nRefreshToken.value = data.refresh_token
    if (data.permissions) adminPermissions.value = data.permissions
  } else {
    nToken.value = ""
    nRefreshToken.value = ""
    adminPermissions.value = {}
    for (const fn of _sessionExpiredListeners) {
      try { fn() } catch { /* listener errors must not break the request path */ }
    }
  }
})

export async function checkNAuth() {
  try {
    const data = await API.get("/api/n/me")
    nLoggedIn.value = !!data.logged_in
    nUser.value = data.logged_in ? { id: data.id, display_name: data.display_name } : null
    nPermissions.value = data.permissions || {}
    // also load permissions from stored JWT if present
    if (nToken.value) {
      try {
        const ui = await API.get("/api/auth/userinfo")
        adminPermissions.value = ui.permissions || {}
      } catch { /* token expired or invalid */ clearTokens() }
    }
  } catch {
    nLoggedIn.value = false
    nUser.value = null
    nPermissions.value = {}
  }
}

// ---------------------------------------------------------------------------
// PKCE helpers (RFC 7636)
// ---------------------------------------------------------------------------

function _base64urlEncode(buffer) {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "")
}

export async function generateCodeVerifier() {
  const arr = new Uint8Array(32)
  crypto.getRandomValues(arr)
  return _base64urlEncode(arr)
}

export async function generateCodeChallenge(verifier) {
  const enc = new TextEncoder().encode(verifier)
  const hash = await crypto.subtle.digest("SHA-256", enc)
  return _base64urlEncode(hash)
}

/**
 * Full PKCE flow: authorize (requires active session) → exchange → store tokens.
 * Returns the permissions dict.
 */
export async function acquireTokensPKCE() {
  const verifier   = await generateCodeVerifier()
  const challenge  = await generateCodeChallenge(verifier)

  // Step 1 — get auth code (nutritionist session must be active)
  const { code } = await API.post("/api/auth/authorize", {
    code_challenge: challenge,
    code_challenge_method: "S256",
  })

  // Step 2 — exchange code + verifier for tokens
  const res = await API.post("/api/auth/token", {
    grant_type:    "authorization_code",
    code,
    code_verifier: verifier,
  })

  storeTokens(res.access_token, res.refresh_token, res.permissions)
  adminPermissions.value = res.permissions || {}
  return res.permissions
}

/**
 * Initiate Zitadel login: fetch config, build PKCE authorize URL, redirect.
 * Stores code_verifier + state in sessionStorage for OidcCallback.js to pick up.
 *
 * @param {Object} [opts]
 * @param {string} [opts.loginHint] — pre-fill the login field (e.g. user's email)
 */
export async function startZitadelLogin(opts = {}) {
  const cfg = await API.get("/api/auth/oidc/config")
  if (!cfg.enabled) throw new Error("OIDC not configured — contact the administrator")

  const verifier  = await generateCodeVerifier()
  const challenge = await generateCodeChallenge(verifier)
  const state     = _base64urlEncode(crypto.getRandomValues(new Uint8Array(16)).buffer)

  sessionStorage.setItem("oidc_code_verifier", verifier)
  sessionStorage.setItem("oidc_state",         state)

  const redirect_uri = window.location.origin + "/auth/callback"
  const params = new URLSearchParams({
    response_type:          "code",
    client_id:              cfg.client_id,
    redirect_uri,
    scope:                  cfg.scope || "openid profile email urn:zitadel:iam:org:project:roles",
    code_challenge:         challenge,
    code_challenge_method:  "S256",
    state,
  })
  if (opts.loginHint) {
    params.set("login_hint", opts.loginHint)
  }
  window.location.href = cfg.issuer.replace(/\/$/, "") + "/oauth/v2/authorize?" + params.toString()
}
